import { useState } from 'react';

import { useNavigate, useParams } from 'react-router';

import useAuth from '@/hooks/useAuth';
import useSystemPlans from '@/hooks/useSystemPlans';

import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import UnauthorizedModal from '@/components/popups/UnauthorizedModal';

import { routes } from '@/constants';
import Constants from '@/constants';

import { useAddReadOnlyPlan } from '../hooks/useAddReadOnlyPlan/useAddReadOnlyPlan';

import { Plan } from '@/_generated/api-clients/xp';
import PageNotFound404 from '@/routes/404/404';
import PreviewTemplateModal from '@/routes/TrainingPlans/CopyTemplateModal/PreviewTemplateModal';
import useGetManagePlansData from '@/routes/TrainingPlans/hooks/useGetManagePlansData';

/**
 * AddReadOnlyPlanPage
 *
 * Route component for `/plans/add/:planId` that handles direct links to add Read-Only plans.
 * - Shows UnauthorizedModal if user lacks Leader/Operator/Admin/Stakeholder permissions
 * - Shows 404 if plan not found or not a readonly plan
 * - Shows PreviewTemplateModal with add flow for readonly plans
 * - Handles compliance plan confirmation via EnableCompliancePlanModal
 */
const AddReadOnlyPlanPage: React.FC = () => {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const {
    data: auth,
    isFetching: isFetchingAuth,
    isError: isAuthError,
  } = useAuth();

  // Check if user has at least Leader permissions
  const hasPermission =
    auth?.highestPermissionLevel === Constants.USER_PERMISSIONS.ADMIN ||
    auth?.highestPermissionLevel === Constants.USER_PERMISSIONS.OPERATOR ||
    auth?.highestPermissionLevel === Constants.USER_PERMISSIONS.LEADER ||
    auth?.highestPermissionLevel === Constants.USER_PERMISSIONS.STAKEHOLDER;

  // Check if user has at least Leader permissions at any location
  // This is the same check used by useSystemPlans to enable the query
  const isAtLeastLeader =
    (auth?.allLocationsWithAtLeastLeaderPermissions.length ?? 0) > 0;

  // Auth is loaded when:
  // - Fetching is complete AND we have a userId (successful auth), OR
  // - Auth errored (user is not authenticated)
  const isAuthLoaded = (!isFetchingAuth && !!auth?.userId) || isAuthError;

  const { isFetchingPlans } = useGetManagePlansData();

  const {
    systemPlans,
    isFetchingSystemPlans,
    isPendingSystemPlans,
    systemPlansError,
  } = useSystemPlans();

  const handleClose = () => {
    navigate(routes.plans.managedPlans);
  };

  const [showPreview, setShowPreview] = useState(true);

  const { subscribeReadonlyPlan, isPending, complianceModal } =
    useAddReadOnlyPlan({ onCancel: handleClose });

  const handleAdd = (template: Plan) => {
    const isCompliance =
      template.category === 'compliance' && template.readonly;
    if (isCompliance) {
      setShowPreview(false);
    }
    subscribeReadonlyPlan(template);
  };

  const plan = systemPlans.find(p => p.id === planId);

  const handleViewProgress = () => {
    if (planId) {
      navigate(routes.plans.managePlan({ planId }));
    }
  };

  if (!isAuthLoaded) {
    return <LoadingOverlay isOpen />;
  }

  if (!hasPermission || !isAtLeastLeader) {
    return <UnauthorizedModal isOpen />;
  }

  const isLoading =
    isFetchingSystemPlans ||
    isPendingSystemPlans ||
    isFetchingPlans ||
    (systemPlans.length === 0 && !systemPlansError);

  if (isLoading) {
    return <LoadingOverlay isOpen />;
  }

  // Show 404 if plan not found, has error, or is not a readonly plan
  if (systemPlansError || !plan?.readonly) {
    return <PageNotFound404 />;
  }

  return (
    <>
      <PreviewTemplateModal
        isOpen={showPreview}
        onClose={handleClose}
        onCopy={() => {
          // Not used for readonly plans, but required by interface
        }}
        onTemplateBackButtonClick={handleClose}
        plan={plan}
        onAdd={handleAdd}
        isLoading={isPending}
        onViewProgress={handleViewProgress}
      />
      {complianceModal}
    </>
  );
};

export default AddReadOnlyPlanPage;
